import React, { useEffect, useState } from 'react'
import { object, arrayOf } from 'prop-types'

import API from 'utils/API'
import intl from 'react-intl-universal'
import { branch } from 'baobab-react/higher-order'
import config from 'config'
import triggerEvent from 'utils/dataLayer'

import BaseTemplate from 'components/templates/BaseTemplate'
import SimpleListSection from 'components/molecules/SimpleListSection'
import VideoModal from 'components/molecules/VideoModal'
import KoinworksBar from 'components/organisms/KoinworksBar'
import SectionRelatedArticles from 'components/organisms/SectionRelatedArticles'
import BannerList from 'components/organisms/BannerList'
import StickyBottomSchool from 'components/organisms/StickyBottomSchool'

import SchoolPageBanner from './SchoolPageBanner'
import SchoolPageBannerContent from './SchoolPageBannerContent'
import SchoolProfile from './SchoolProfile'
import SchoolFaculties from './SchoolFaculties'
import SchoolRegistration from './SchoolRegistration'
import SchoolTuition from './SchoolTuition'
import SchoolScholarship from './SchoolScholarship'
import SchoolLocations from './SchoolLocations'
import SchoolVideos from './SchoolVideos'
import SchoolTestimonies from './SchoolTestimonies'

const { assets_url: assetsUrl } = config

const SchoolDetail = ({ school, testimonies }) => {
  const {
    id,
    name,
    article_tags: articleTags,
    banners,
    campus_sector: campusSector,
    campuses,
    facilities,
    faculties,
    images,
    liked,
    logo,
    paid,
    brochure,
    entrance_exam: registrationInfo,
    registration_url: registrationUrl,
    tuition_fee: tuitionInfo,
    scholarship,
    slug,
    profile: {
      accreditation,
      description,
      email,
      phone_number: phoneNumber,
      social_media: socialMedia,
      student_count: studentCount,
      teacher_count: teacherCount,
      teacher_student_ratio: teacherStudentRatio,
      tuition_fee_snippet: tuitionSnippet,
      website,
    },
    videos,
  } = school

  const bigBannerCount = banners.filter(banner => banner.size === 'big').length

  const tagsQuery = articleTags.join('&tags[]=')
  const articlesSource = `${API.endPoints().articles}?tags[]=${tagsQuery}`

  const [showVideo, setShowVideo] = useState(false)
  const [videoSource, setVideoSource] = useState('')

  const nonPrimaryVideos = videos.filter(v => !v.primary).map(video => ({
    ...video,
    thumbnail: video.thumbnail.thumbnail_sm,
    source: video.parsed_source.source,
  }))

  const mainVideo = videos.filter(v => v.primary).map(video => ({
    ...video,
    thumbnail: video.thumbnail.thumbnail_lg,
    source: video.parsed_source.source,
  }))[0]

  const handleVideoClick = (source, title) => {
    setShowVideo(true)
    setVideoSource(source)
    triggerEvent(`ga/campus/card-video/${title}`)
  }

  const facultiesProps = faculties.map(faculty => ({
    ...faculty,
    path: `/directory/${slug}/faculties/${faculty.slug}`,
  }))
  const facilitiesProps = Object.keys(facilities).slice(0, 30).map(key => `${key} ${facilities[key] ? `(${facilities[key]})` : ''}`)

  const sections = [
    {
      target: '#profile',
      text: 'Informasi',
      show: true,
    },
    {
      target: '#faculties',
      text: 'Fakultas',
      show: faculties.length > 0,
    },
    {
      target: '#registration',
      text: 'Registrasi',
      show: !!registrationInfo,
    },
    {
      target: '#tuition',
      text: 'Biaya',
      show: !!tuitionInfo,
    },
    {
      target: '#scholarship',
      text: 'Beasiswa',
      show: !!scholarship,
    },
    {
      target: '#testimonies',
      text: 'Testimoni',
      show: testimonies && testimonies.length > 0,
    },
    {
      target: '#locations',
      text: 'Lokasi',
      show: campuses.length > 0,
    },
    {
      target: '#facilities',
      text: 'Fasilitas',
      show: Object.keys(facilities).length > 0,
    },
    {
      target: '#videos',
      text: 'Video',
      show: videos.length > 0,
    },
    {
      target: '#related-articles',
      text: 'Artikel',
      show: articleTags.length > 0,
    },
  ]

  const [showKoinworksBar, setShowKoinworksBar] = useState(false)
  useEffect(() => {
    API.get(API.endPoints().darklaunch.get, { key: 'enable-koinworks-widget' }).then((res) => {
      const status = res.data.enabled || false
      setShowKoinworksBar(status)
    })
  }, [])

  return (
    <BaseTemplate
      sections={sections}
      firstSection=".school-page-banner"
      backToTopPostAdjusment={603}
      stickyBottomContent={
        <StickyBottomSchool
          logo={logo}
          name={name}
          paid={paid}
          slug={slug}
          registrationUrl={registrationUrl}
          brochureUrl={brochure}
          likeStatus={liked}
          likeTarget={id}
          type="campus"
        />
      }
      actionContent={{
        likeStatus: liked,
        likeTarget: id,
        kind: 'schools',
        type: 'campus',
      }}
      useNewHeader
      useBackToTop
      useCTABottom={Boolean(registrationUrl || brochure)}
      useStickyBottom
    >
      <div className="school-detail-page">
        <SchoolPageBanner
          title={name}
          slug={slug}
          subtitle={intl.get(`school.kind.${campusSector}`)}
          likeStatus={liked}
          likeTarget={id}
          logo={logo}
          kind="schools"
          imgSrc={images[0] && images[0].original_url}
          mainVideo={mainVideo}
          onVideoClick={handleVideoClick}
          content={
            <SchoolPageBannerContent
              brochure={brochure}
              socialMedia={socialMedia}
              registrationUrl={registrationUrl}
            />
          }
        />
        { (banners.length > 0) && <BannerList banners={banners} marginBottom={80} usePlaceholder={false} type="campus" /> }
        <SchoolProfile
          accreditation={accreditation}
          brochure={brochure}
          email={email}
          description={description}
          images={images}
          phoneNumber={phoneNumber}
          studentCount={studentCount}
          teacherCount={teacherCount}
          teacherStudentRatio={teacherStudentRatio}
          tuitionFee={tuitionSnippet}
          website={website}
        />
        { (faculties.length > 0) && <SchoolFaculties faculties={facultiesProps} /> }
        {
          registrationInfo &&
          <SchoolRegistration
            registrationInfo={registrationInfo}
            registrationUrl={registrationUrl}
          />
        }
        { tuitionInfo && <SchoolTuition tuitionInfo={tuitionInfo} /> }
        { scholarship && <SchoolScholarship scholarship={scholarship} /> }
        { (testimonies && testimonies.length > 0) && <SchoolTestimonies testimonies={testimonies} /> }
        { (campuses.length > 0) && <SchoolLocations campuses={campuses} /> }
        {
          (facilitiesProps.length > 0) &&
          <SimpleListSection
            id="facilities"
            list={facilitiesProps}
            title="Fasilitas"
            backgroundUrl={`${assetsUrl}/assets/img/facilities_bg/school-facilities-2.jpg`}
          />
        }
        {
          (videos.length > 0) &&
          <SchoolVideos videos={[mainVideo, ...nonPrimaryVideos].filter(v => v).slice(0, 3)} handleVideoClick={handleVideoClick} />
        }
        { (articleTags.length > 0) && <SectionRelatedArticles sourceUrl={articlesSource} type="campus" /> }
        {showKoinworksBar && <KoinworksBar/>}
        <VideoModal
          isOpen={showVideo}
          onVideoClose={() => setShowVideo(false)}
          videoSource={videoSource}
        />
      </div>
      <style jsx>
        {`
          .school-detail-page {
            background-color: #fff;
            color: #0c2846;
            :global(.banner-link-small) {
              @media screen and (min-width: 768px) {
                display: none;
              }
            }
            :global(#related-articles) {
              background: #f6f7fa;
              padding-top: 80px;
              padding-bottom: 40px;
            }
            :global(#banner-list) {
              @media screen and (min-width: 768px) {
                ${!bigBannerCount ? 'display: none' : ''}
              }
            }
          }
        `}
      </style>
    </BaseTemplate>
  )
}

SchoolDetail.propTypes = {
  school: object,
  testimonies: arrayOf(object),
}

export { SchoolDetail }

export default branch({
  school: ['serverProps', 'school'],
  testimonies: ['serverProps', 'testimonies'],
}, SchoolDetail)
